import { UseInfiniteQueryResult, UseQueryResult } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import Link from 'next/link'
import { Fragment, useMemo } from 'react'
import { BiLinkExternal, BiWallet } from 'react-icons/bi'
import { InView } from 'react-intersection-observer'
import Alert from 'components/Alert'
import AlgoPrice from 'components/AlgoPrice'
import Loading from 'components/Loading'
import UserThumbnail from 'components/UserThumbnail'
import { InfiniteAnalyticsQueryResult } from 'api/hooks/useInfiniteAnalytics'
// import Filters from './Filters'
import { useExplorerStore } from 'store/index'
import { classNames, truncateAddress } from 'helpers/utilities'
import type { NFDAnalyticRecord, NfdRecordCollection } from 'api/api-client'
import type { ICustomError } from 'api/customError'

interface InfiniteSalesProps {
  query: UseInfiniteQueryResult<InfiniteAnalyticsQueryResult, ICustomError>
  usersQuery: UseQueryResult<NfdRecordCollection, AxiosError>
  totalSales: number
  fetchedPages?: Set<number>
  refetchPage?: (page: number) => void
}

export default function InfiniteSales({
  query,
  usersQuery,
  totalSales,
  fetchedPages,
  refetchPage
}: InfiniteSalesProps) {
  const { data, error, isFetchingNextPage, isInitialLoading, fetchNextPage, hasNextPage } = query

  const allSales = useMemo(() => {
    return data?.pages?.flatMap((page) => page.data.results).filter((sale) => sale !== null) || []
  }, [data])

  const selectedExplorer = useExplorerStore((state) => state.selectedExplorer)
  const lookupByGroupId = useExplorerStore((state) => state.lookupByGroupId)

  const renderAddress = (address: string | undefined) => {
    if (!address) {
      return <span className="mt-1 text-sm text-gray-700 dark:text-gray-500">N/A</span>
    }

    const nfd = usersQuery.data?.find((user) => user.caAlgo?.some((addr) => addr === address))

    if (nfd) {
      return <UserThumbnail nfd={nfd} className="text-gray-900 dark:text-gray-300" link />
    }

    const addr = truncateAddress(address, { array: true })

    return (
      <Link
        href={`/address/${address}`}
        className="inline-flex items-center text-sm text-gray-900 hover:text-brand-500 font-mono dark:text-gray-400 dark:hover:text-brand-500"
      >
        <BiWallet className="flex-shrink-0 mr-1.5 h-5 w-5 my-1.5" aria-hidden="true" />
        {addr[0]}&hellip;{addr[1]}
      </Link>
    )
  }

  const renderRowCells = (result: NFDAnalyticRecord, i: number) => {
    return (
      <>
        <td
          className={classNames(
            i !== totalSales - 1 ? 'border-b border-gray-200 dark:border-gray-700/40' : '',
            'w-full max-w-0 py-4 pl-4 pr-3 text-base font-medium text-gray-900 sm:pl-6 md:w-[25vw] md:max-w-[25vw] xl:w-auto xl:max-w-none truncate dark:text-gray-300'
          )}
        >
          <Link
            href={`/name/${result.data?.name}`}
            className="text-gray-900 hover:text-brand-500 outline-brand-500 text-base font-medium dark:text-gray-300 dark:hover:text-brand-500"
          >
            {result.data?.name}
          </Link>
        </td>
        <td
          className={classNames(
            i !== totalSales - 1 ? 'border-b border-gray-200 dark:border-gray-700/40' : '',
            'whitespace-nowrap py-4 pl-4 pr-3 font-medium text-gray-900 sm:pl-6 dark:text-gray-300'
          )}
        >
          <AlgoPrice price={result.price} className="xl:hidden" />
          <AlgoPrice price={result.price} className="hidden xl:inline" />
        </td>
        <td
          className={classNames(
            i !== totalSales - 1 ? 'border-b border-gray-200 dark:border-gray-700/40' : '',
            'hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell'
          )}
        >
          {renderAddress(result.data?.buyer)}
        </td>
        <td
          className={classNames(
            i !== totalSales - 1 ? 'border-b border-gray-200 dark:border-gray-700/40' : '',
            'whitespace-nowrap py-4 pl-3 pr-4 w-16 text-center text-sm font-medium sm:pr-6 xl:w-56 xl:text-left'
          )}
        >
          {result.data?.groupID && result.data?.block ? (
            <a
              href={lookupByGroupId(result.data.groupID, result.data.block)}
              className="inline-flex items-center text-gray-500 hover:text-gray-900 font-medium outline-brand-500 group dark:hover:text-gray-400"
              target="_blank"
              rel="noreferrer"
            >
              <span className="hidden xl:inline">{dayjs(result.timestamp).format('lll')}</span>
              <BiLinkExternal className="m-2 xl:m-0 xl:ml-3 w-5 h-5 text-gray-400 group-hover:text-brand-500 dark:text-gray-600 dark:hover:text-brand-500" />
            </a>
          ) : (
            <span className="hidden xl:inline">{dayjs(result.timestamp).format('lll')}</span>
          )}
        </td>
      </>
    )
  }

  const renderRows = (results: NFDAnalyticRecord[] | null, pageIdx: number) => {
    if (!data || !results) {
      return null
    }

    return results.map((result: NFDAnalyticRecord, i) => {
      const handleInViewChange = (inView: boolean) => {
        if (!inView) return

        const isLastPage = pageIdx === data.pages.length - 1
        const needsRefresh = refetchPage && !fetchedPages?.has(pageIdx)

        if (isLastPage && hasNextPage) {
          fetchNextPage()
        } else if (needsRefresh) {
          refetchPage(pageIdx)
        }
      }

      const key = `${result.data?.name}-${result.price}-${result.timestamp}`
      const isLastResult = i === results.length - 1

      if (isLastResult) {
        return (
          <InView key={key} as="tr" onChange={handleInViewChange}>
            {renderRowCells(result, i)}
          </InView>
        )
      }

      return <tr key={key}>{renderRowCells(result, i)}</tr>
    })
  }

  const renderTable = () => {
    return (
      <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle md:px-8">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table
              key={selectedExplorer}
              className="min-w-full border-separate"
              style={{ borderSpacing: 0 }}
            >
              <thead className="relative bg-gray-100 dark:bg-gray-850/50">
                <tr>
                  <th
                    scope="col"
                    className="sticky top-0 z-10 border-b border-gray-300 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 dark:text-gray-500 dark:border-transparent"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="sticky top-0 z-10 border-b border-gray-300 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 dark:text-gray-500 dark:border-transparent"
                  >
                    Price
                  </th>
                  <th
                    scope="col"
                    className="hidden sticky top-0 z-10 border-b border-gray-300 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell dark:text-gray-500 dark:border-transparent"
                  >
                    Buyer
                  </th>
                  <th
                    scope="col"
                    className="sticky top-0 z-10 border-b border-gray-300 pl-3 pr-4 sm:pr-6 py-3.5 text-center text-sm font-semibold text-gray-900 xl:text-left dark:text-gray-500 dark:border-transparent"
                  >
                    <span className="xl:hidden">Txn</span>
                    <span className="hidden xl:inline">Transaction</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:bg-gray-850 dark:divide-gray-700/40">
                {data?.pages.map((page, pageIdx) => (
                  <Fragment key={pageIdx}>{renderRows(page.data.results, pageIdx)}</Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  const renderContent = () => {
    if (isInitialLoading || usersQuery.isInitialLoading) {
      return (
        <div className="absolute inset-x-0 bottom-0 top-0 sm:top-24 flex items-center justify-center">
          <Loading />
        </div>
      )
    }

    if (error) {
      return (
        <div className="py-5">
          <Alert type="error" title="Error" message="An error occurred while fetching sales." />
        </div>
      )
    }

    if (allSales.length === 0) {
      return (
        <div className="py-5">
          <Alert type="info" title="No sales" message="There are currently no sales." />
        </div>
      )
    }

    return <div className="sm:mt-6 flex flex-col">{renderTable()}</div>
  }

  return (
    <div>
      {/* search not yet supported */}
      {/* <Filters
        key={viewId}
        search={search}
        placeholder={searchPlaceholder}
        className="sm:-mt-6 sm:mb-6"
      /> */}

      <div className="sm:flex md:items-center">
        <div className="hidden sm:block flex-auto">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Sales</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-500">
            List of sales made by this address
          </p>
        </div>
      </div>
      {renderContent()}

      {/* @todo: show discreet table loader */}
      {isFetchingNextPage && (
        <div className="flex flex-col items-center justify-center py-32">
          <Loading />
        </div>
      )}
    </div>
  )
}
